import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { newsList } from '@/mocks/news';
import { getHotMovies } from '@/utils/media';
import { usePageMeta } from '@/hooks/usePageMeta';

type Tab = '全部' | '影视' | '资讯';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState<Tab>('全部');

  useEffect(() => {
    setInput(q);
  }, [q]);

  usePageMeta(
    q ? `搜索“${q}” - 红桃影视` : '搜索影视与资讯 - 红桃影视',
    q
      ? `红桃影视关于“${q}”的搜索结果，包含相关影视作品与资讯内容。`
      : '在红桃影视搜索你感兴趣的电影、电视剧与影视资讯。',
  );

  const keyword = q.trim().toLowerCase();

  const movieResults = useMemo(() => {
    if (!keyword) return [];
    return movies.filter((m) =>
      [m.title, m.director, m.summary, m.genres.join(' '), m.actors.join(' ')]
        .join(' ')
        .toLowerCase()
        .includes(keyword),
    );
  }, [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    return newsList.filter((n) =>
      [n.title, n.summary, n.content.join(' '), n.category].join(' ').toLowerCase().includes(keyword),
    );
  }, [keyword]);

  const total = movieResults.length + newsResults.length;
  const showMovies = tab === '全部' || tab === '影视';
  const showNews = tab === '全部' || tab === '资讯';
  const recommendations = getHotMovies().slice(0, 6);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    setSearchParams(next ? { q: next } : {});
  };

  const tabs: { key: Tab; count: number }[] = [
    { key: '全部', count: total },
    { key: '影视', count: movieResults.length },
    { key: '资讯', count: newsResults.length },
  ];

  return (
    <div className="pb-6">
      <div className="border-b border-background-300/60 bg-background-100">
        <div className="px-4 md:px-6 lg:px-10 py-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
            搜索结果
          </h1>
          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row items-stretch gap-3 max-w-3xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视名称、类型或关键词"
                className="w-full h-12 pl-11 pr-4 rounded-md bg-background-200 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-8 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
          {q && (
            <p className="mt-4 text-sm text-foreground-600">
              关键词 <span className="text-primary-400 font-medium">“{q}”</span> 共找到{' '}
              <span className="text-accent-500 font-semibold">{total}</span> 条结果
            </p>
          )}
        </div>
      </div>

      <div className="px-4 md:px-6 lg:px-10 mt-6">
        {q && (
          <div className="flex flex-wrap items-center gap-2">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  tab === t.key
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-200 text-foreground-700 hover:bg-background-300'
                }`}
              >
                {t.key} ({t.count})
              </button>
            ))}
          </div>
        )}

        {!q ? (
          <div className="mt-8">
            <p className="text-foreground-600">输入关键词开始搜索，或看看这些热门内容：</p>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {recommendations.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        ) : total === 0 ? (
          <div className="mt-8">
            <div className="rounded-lg bg-background-100 border border-background-300/60 py-16 flex flex-col items-center text-center">
              <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-3xl text-foreground-500">
                <i className="ri-search-eye-line" />
              </span>
              <p className="mt-4 text-foreground-700">没有找到与“{q}”相关的内容</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词试试，比如“悬疑”“江湖笔录”</p>
            </div>
            <h2 className="mt-10 text-lg font-semibold text-foreground-950 mb-5">你可能感兴趣</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {recommendations.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-10">
            {showMovies && movieResults.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-film-line text-primary-400" />
                  影视作品（{movieResults.length}）
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {movieResults.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </section>
            )}

            {showNews && newsResults.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-newspaper-line text-accent-500" />
                  资讯内容（{newsResults.length}）
                </h2>
                <div className="space-y-3">
                  {newsResults.map((article) => (
                    <Link
                      key={article.id}
                      to={`/news/${article.id}`}
                      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-100 border border-background-300/60 hover:border-primary-500/50 transition-colors"
                    >
                      <span className="w-full sm:w-40 shrink-0 aspect-[16/9] rounded-md overflow-hidden bg-background-200">
                        <img
                          src={article.cover}
                          alt={article.title}
                          title={`${article.title} 资讯`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-base font-medium text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2">
                          {article.title}
                        </span>
                        <span className="block mt-2 text-sm text-foreground-600 line-clamp-2">
                          {article.summary}
                        </span>
                        <span className="block mt-2 text-xs text-foreground-500">
                          {article.category} · {article.date}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {showMovies && showNews && movieResults.length === 0 && newsResults.length === 0 && (
              <p className="text-foreground-600">暂无相关内容。</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}